import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plane, ArrowRight, Users, Clock, LogIn, LogOut, Loader2, Activity, Timer } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { FlightTimesDialog } from '../components/crew/FlightTimesDialog';
import { posLabel } from '../components/crew/crewPositions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POS = { captain: 'Capitán (PIC)', first_officer: 'Primer oficial (FO)', flight_attendant: 'Tripulante de cabina (TCP)' };
const hhmm = (s) => (s ? String(s).slice(11, 16) : '—');
const fmt = (iso, tz) => (iso ? new Intl.DateTimeFormat('es', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso)) : '—');
const dur = (m) => `${Math.floor((m || 0) / 60)} h ${String((m || 0) % 60).padStart(2, '0')} min`;

function DutyCard({ duty, busy, onIn, onOut }) {
    const tz = duty.timezone;
    const open = duty.open_record;
    return (
        <Card className="card-aviation" data-testid="duty-card"><CardContent className="p-5 flex flex-wrap items-center gap-6">
            <div className="flex-1 min-w-[220px]">
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Servicio de hoy</p>
                {!open && duty.closed_today && <p className="text-sm text-muted-foreground" data-testid="duty-closed-today">Servicio de hoy cerrado: {fmt(duty.closed_today.duty_start, tz)} – {fmt(duty.closed_today.duty_end, tz)} ({dur(duty.closed_today.duty_minutes)})</p>}
                {open ? <p className="text-lg font-semibold text-emerald-700 dark:text-emerald-400" data-testid="duty-open">En servicio desde {fmt(open.duty_start, tz)}{open.started_by?.on_behalf ? ` (registrado por ${open.started_by.name})` : ''}</p>
                    : duty.first_departure ? <p className="text-lg font-semibold" data-testid="duty-closed">Presentación disponible desde {fmt(duty.checkin_opens_at, tz)}</p>
                        : <p className="text-lg font-semibold text-muted-foreground" data-testid="duty-none">Sin vuelos confirmados hoy</p>}
                <p className="text-xs text-muted-foreground mt-1">Se habilita {duty.lead_minutes} min antes de la primera salida. Solo informativo: no se bloquea nada por límites.</p>
            </div>
            <div className="flex gap-6 font-mono text-sm" data-testid="duty-accumulated">
                <div><p className="text-[10px] uppercase text-muted-foreground">Actual</p><p className="font-bold">{dur(duty.accumulated.current_min)}</p></div>
                <div><p className="text-[10px] uppercase text-muted-foreground">7 días</p><p className="font-bold">{dur(duty.accumulated.last_7d_min)}</p></div>
                <div><p className="text-[10px] uppercase text-muted-foreground">28 días</p><p className="font-bold">{dur(duty.accumulated.last_28d_min)}</p></div>
            </div>
            <div className="flex gap-2">
                <Button onClick={onIn} disabled={busy || !duty.can_check_in} data-testid="duty-check-in"><LogIn className="w-4 h-4 mr-2" />Presentarme</Button>
                <Button variant="secondary" onClick={onOut} disabled={busy || !duty.can_check_out} data-testid="duty-check-out"><LogOut className="w-4 h-4 mr-2" />Fin de servicio</Button>
            </div>
        </CardContent></Card>
    );
}

function FlightCard({ f, today, isPilot, onTimes, navigate }) {
    return (
        <Card className="card-aviation" data-testid={`my-flight-${f.id}`}><CardContent className="p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
                <Plane className="w-4 h-4 text-primary" />
                <span className="font-mono font-bold text-lg">{f.flight_number}</span>
                <span className="flex items-center gap-1 font-mono text-sm">{f.origin}<ArrowRight className="w-3 h-3" />{f.destination}</span>
                <span className="flex items-center gap-1 text-sm text-muted-foreground"><Clock className="w-3.5 h-3.5" />{hhmm(f.scheduled_departure)} – {hhmm(f.scheduled_arrival)}</span>
                <Badge variant="outline" className="font-mono">{f.aircraft_registration || 'Sin aeronave'}{f.aircraft_type ? ` · ${f.aircraft_type}` : ''}</Badge>
                <Badge variant="outline">Base {f.base}</Badge>
                <Badge className="bg-primary/10 text-primary" data-testid={`my-position-${f.id}`}>{f.my_flight_position ? posLabel(f.my_flight_position) : (POS[f.my_position] || f.my_position)}</Badge>
                {f.training_flight && <Badge variant="outline" className="border-sky-500/40 text-sky-700 dark:text-sky-400" data-testid={`my-training-${f.id}`}>Vuelo de instrucción</Badge>}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid={`my-crew-${f.id}`}>
                <Users className="w-3.5 h-3.5" />{f.crew.map((c, i) => <span key={i}>{c.name} · {c.flight_position ? posLabel(c.flight_position) : (POS[c.position] || c.position)}{c.role === 'deadhead_op' ? ' (DH)' : ''}</span>)}
            </div>
            {f.date === today && (
                <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => navigate(`/turnaround/flight/${f.id}`)} data-testid={`my-turnaround-${f.id}`}><Activity className="w-3.5 h-3.5 mr-1" />Turnaround</Button>
                    {isPilot && <Button size="sm" variant="outline" onClick={() => onTimes(f)} data-testid={`my-times-${f.id}`}><Timer className="w-3.5 h-3.5 mr-1" />Reportar tiempos</Button>}
                </div>
            )}
        </CardContent></Card>
    );
}

export default function MyFlightsPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [duty, setDuty] = useState(null);
    const [busy, setBusy] = useState(false);
    const [timesFor, setTimesFor] = useState(null);
    const load = useCallback(async () => {
        try {
            const [f, d] = await Promise.all([axios.get(`${API}/my/flights`), axios.get(`${API}/my/duty`)]);
            setData(f.data); setDuty(d.data);
        } catch (e) { toast.error(e.response?.data?.detail || 'No se pudieron cargar tus vuelos'); setData({ flights: [] }); }
    }, []);
    useEffect(() => { load(); }, [load]);
    const act = async (path, msg) => {
        setBusy(true);
        try { await axios.post(`${API}/my/duty/${path}`); toast.success(msg); await load(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
        finally { setBusy(false); }
    };
    if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
    const groups = data.flights.reduce((acc, f) => ({ ...acc, [f.date]: [...(acc[f.date] || []), f] }), {});
    return (
        <div className="space-y-6" data-testid="my-flights-page">
            <div>
                <h1 className="text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']">Mis vuelos</h1>
                <p className="text-muted-foreground mt-1">{data.crew?.name} · {POS[data.crew?.position] || data.crew?.position} · solo vuelos con tu asignación confirmada</p>
            </div>
            {duty && <DutyCard duty={duty} busy={busy} onIn={() => act('check-in', 'Presentación registrada')} onOut={() => act('check-out', 'Fin de servicio registrado')} />}
            {!data.flights.length ? <p className="text-muted-foreground" data-testid="my-flights-empty">No tienes vuelos confirmados en los próximos días.</p>
                : Object.entries(groups).map(([day, fs]) => (
                    <div key={day} className="space-y-2">
                        <h2 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">{day === data.today ? `Hoy · ${day}` : day}</h2>
                        {fs.map(f => <FlightCard key={f.id} f={f} today={data.today} isPilot={user?.role === 'pilot'} onTimes={setTimesFor} navigate={navigate} />)}
                    </div>
                ))}
            <FlightTimesDialog flight={timesFor} tz={data.timezone} onClose={() => setTimesFor(null)} />
        </div>
    );
}
